import { useCallback, useEffect, useRef, useState } from "react";
import { CaretLeft, CaretRight, Gear, CalendarPlus, Gauge, SealCheck, EnvelopeSimple, Megaphone } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { MonthRows } from "../../components/regularisation/TeamMonthPanel";
import { EmployeeMonthDrawer } from "../../components/regularisation/EmployeeMonthDrawer";
import { SettingsDialog, ExtensionDialog, FinaliseResultDialog, HoursCheckDialog, EmailLogDialog } from "../../components/regularisation/MonthCloseDialogs";
import { LaunchNoticeDialog } from "../../components/regularisation/LaunchNoticeDialog";
import { currentPeriodIST, shiftPeriod, periodLabel } from "../../lib/regularisation";

const FILTERS = [["", "All", "total"], ["ready", "Ready to finalise", "ready"], ["held", "Held (pending dates)", "held"],
    ["Provisional", "Provisional", "Provisional"], ["Awaiting Approval", "Awaiting approval", "Awaiting Approval"], ["Finalised", "Finalised", "Finalised"]];

export default function AttendanceMonthClose() {
    const { user } = useAuth();
    const isSA = user?.role === "super_admin";
    const [period, setPeriod] = useState(shiftPeriod(currentPeriodIST(), -1));
    const [filter, setFilter] = useState("");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState([]);
    const [open, setOpen] = useState(null);
    const [dlg, setDlg] = useState(null);
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const seq = useRef(0);
    const load = useCallback(async () => {
        const my = ++seq.current;
        try {
            const { data: d } = await api.get("/attendance/month-close/overview", { params: { period, status_filter: filter, search, page, page_size: 50 }, timeout: 120000 });
            if (my === seq.current) setData(d);
        } catch (err) {
            if (my === seq.current) toast.error(formatApiError(err, "Could not load month close"));
        }
    }, [period, filter, search, page]);
    useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [load]);
    useEffect(() => { setSelected([]); setPage(1); }, [period, filter, search]);

    const finalise = async () => {
        setBusy(true);
        try {
            const { data: r } = await api.post("/attendance/month-close/finalise", { period, employee_ids: selected }, { timeout: 120000 });
            setResult(r);
            setSelected([]);
            load();
        } catch (err) {
            toast.error(formatApiError(err, "Finalisation failed"));
        } finally {
            setBusy(false);
        }
    };
    const items = data?.items || [];
    const ready = items.filter((r) => r.ready_to_finalise).map((r) => r.employee.id);
    const st = data?.settings || {};

    return (
        <div className="max-w-[1500px]" data-testid="month-close-page">
            <PageHeader
                eyebrow="Attendance · Month close"
                title={`Finalise ${periodLabel(period)}`}
                description="Finalise each employee once nothing is pending. A held employee never blocks anyone else. Payroll uses the finalised month."
                actions={<>
                    <Button variant="outline" size="sm" onClick={() => setPeriod(shiftPeriod(period, -1))} data-testid="mc-prev"><CaretLeft size={14} /></Button>
                    <Button variant="outline" size="sm" disabled={period >= currentPeriodIST()} onClick={() => setPeriod(shiftPeriod(period, 1))} data-testid="mc-next"><CaretRight size={14} /></Button>
                    <Button variant="outline" size="sm" onClick={() => setDlg("extend")} data-testid="mc-extend-btn"><CalendarPlus size={14} className="mr-1" /> Extend deadline</Button>
                    {isSA && <Button variant="outline" size="sm" onClick={() => setDlg("hours")} data-testid="mc-hours-btn"><Gauge size={14} className="mr-1" /> Hours check</Button>}
                    {isSA && <Button variant="outline" size="sm" onClick={() => setDlg("emails")} data-testid="mc-email-log-btn"><EnvelopeSimple size={14} className="mr-1" /> Email log</Button>}
                    {isSA && <Button variant="outline" size="sm" onClick={() => setDlg("settings")} data-testid="mc-settings-btn"><Gear size={14} className="mr-1" /> Settings</Button>}
                    {isSA && <Button variant="outline" size="sm" onClick={() => setDlg("launch")} data-testid="mc-launch-notice-btn"><Megaphone size={14} className="mr-1" /> Launch notice</Button>}
                </>}
            />
            <div className="px-6 sm:px-8 py-5 space-y-4">
                <div className="flex flex-wrap gap-2 text-xs text-slate-600" data-testid="mc-settings-strip">
                    <span className="rounded border border-slate-200 bg-white px-2 py-1">Employee deadline: day {st.employee_deadline_day}</span>
                    <span className="rounded border border-slate-200 bg-white px-2 py-1">Approver deadline: day {st.manager_deadline_day}</span>
                    <span className="rounded border border-slate-200 bg-white px-2 py-1" data-testid="mc-email-mode">Email: {st.email_delivery_mode === "test_only" ? "test recipients only" : st.email_delivery_mode}</span>
                    <span className="rounded border border-slate-200 bg-white px-2 py-1">Payroll gate: {st.payroll_gate === "block_approval" ? "blocks approval" : "warn"} from {st.finalisation_required_from || "—"}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2" data-testid="mc-filters">
                    {FILTERS.map(([k, l, c]) => (
                        <button key={k || "all"} onClick={() => setFilter(k)} className={`rounded-full border px-3 py-1 text-xs ${filter === k ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300"}`} data-testid={`mc-filter-${k || "all"}`}>
                            {l} <b className="ml-1">{data?.counts?.[c] ?? 0}</b>
                        </button>
                    ))}
                    <Input placeholder="Search name or code" value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 w-[220px] ml-auto" data-testid="mc-search" />
                </div>
                <section className="bg-white rounded-xl border border-slate-200 p-4 overflow-x-auto">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-xs text-slate-500">{data?.total ?? 0} employee(s) · {selected.length} selected{data?.capped && <span className="ml-2 text-amber-700" data-testid="mc-capped-note">Showing and counting only the first 1,000 employees (filter counts too) — search by name or code to find others.</span>}</div>
                        <div className="flex gap-2">
                            <Button size="sm" variant="outline" disabled={!ready.length} onClick={() => setSelected(ready)} data-testid="mc-select-ready">Select all ready on page ({ready.length})</Button>
                            <Button size="sm" disabled={!selected.length || busy} onClick={finalise} data-testid="mc-finalise-btn"><SealCheck size={14} className="mr-1" /> Finalise {selected.length || ""}</Button>
                        </div>
                    </div>
                    <MonthRows rows={items} onOpen={(r) => setOpen(r.employee.id)} selectable selected={selected} setSelected={setSelected} />
                    {(data?.total || 0) > 50 && (
                        <div className="mt-3 flex justify-end gap-2 text-xs items-center">
                            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="mc-page-prev">Prev</Button>
                            <span>Page {page} of {Math.ceil(data.total / 50)}</span>
                            <Button variant="outline" size="sm" disabled={page >= Math.ceil(data.total / 50)} onClick={() => setPage((p) => p + 1)} data-testid="mc-page-next">Next</Button>
                        </div>
                    )}
                </section>
            </div>
            <EmployeeMonthDrawer employeeId={open} period={period} isAdmin onClose={() => setOpen(null)} onChanged={load} />
            <SettingsDialog open={dlg === "settings"} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />
            <ExtensionDialog open={dlg === "extend"} period={period} onClose={() => setDlg(null)} onSaved={() => { setDlg(null); load(); }} />
            <HoursCheckDialog open={dlg === "hours"} onClose={() => setDlg(null)} />
            <EmailLogDialog open={dlg === "emails"} onClose={() => setDlg(null)} />
            <LaunchNoticeDialog open={dlg === "launch"} onClose={() => setDlg(null)} />
            <FinaliseResultDialog result={result} onClose={() => setResult(null)} />
        </div>
    );
}
